import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const empty = {
  name: "", description: "", label_id: "",
  dlp_rule_ids: [], min_confidence: 0.7, enabled: true,
};

export default function Classifier() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [labels, setLabels] = useState([]);
  const [dlp, setDlp] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try {
      const [r, l, d] = await Promise.all([
        api.get("/classifier/rules"),
        api.get("/labels"),
        api.get("/dlp/rules"),
      ]);
      setItems(r.data); setLabels(l.data); setDlp(d.data);
    } catch (err) { console.error("classifier load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/classifier/rules", form); toast.success("Classifier rule created"); setOpen(false); setForm(empty); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this classifier rule?")) return;
    try { await api.delete(`/classifier/rules/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleDlp = (id) => {
    const has = form.dlp_rule_ids.includes(id);
    setForm({ ...form, dlp_rule_ids: has ? form.dlp_rule_ids.filter((x) => x !== id) : [...form.dlp_rule_ids, id] });
  };

  const labelById = Object.fromEntries(labels.map((l) => [l.label_id, l]));

  return (
    <Layout title="Classifier Policies" subtitle="LABEL ASSIGNMENT">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="classifier-count">
          {items.length} POLICIES · {items.filter((i) => i.enabled).length} ENABLED
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-classifier-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><Plus className="w-4 h-4" /> NEW POLICY</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xl">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Sparkles className="w-4 h-4 text-cyan-400" /> Create Classifier Policy</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2"><L>Name</L><Input data-testid="cls-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
              <div className="col-span-2"><L>Description</L><Input data-testid="cls-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
              <div className="col-span-2">
                <L>Assign label when matched</L>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {labels.length === 0 && <span className="text-xs text-zinc-500 font-mono">no labels — create some first</span>}
                  {labels.map((l) => {
                    const on = form.label_id === l.label_id;
                    return (
                      <button key={l.label_id} type="button" onClick={() => setForm({ ...form, label_id: on ? "" : l.label_id })}
                        className={"text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm " + (on ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-zinc-700 text-zinc-500 hover:text-zinc-300")}>
                        {l.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="col-span-2">
                <L>Trigger DLP rules</L>
                <div className="flex flex-wrap gap-1.5 mt-1 max-h-24 overflow-y-auto">
                  {dlp.length === 0 && <span className="text-xs text-zinc-500 font-mono">no DLP rules</span>}
                  {dlp.map((r) => {
                    const on = form.dlp_rule_ids.includes(r.rule_id);
                    return (
                      <button key={r.rule_id} type="button" onClick={() => toggleDlp(r.rule_id)}
                        className={"text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm " + (on ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-zinc-700 text-zinc-500 hover:text-zinc-300")}>
                        {r.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div><L>Min confidence (0–1)</L><Input data-testid="cls-conf-input" type="number" step="0.05" min="0" max="1" value={form.min_confidence} onChange={(e) => setForm({ ...form, min_confidence: parseFloat(e.target.value || "0") })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-900">
                <span className="text-sm">Enabled</span>
                <Switch data-testid="cls-enabled-switch" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="submit-cls-btn" onClick={submit} disabled={!form.label_id} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">CREATE</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="classifier-list">
        {items.map((r) => {
          const lab = labelById[r.label_id];
          return (
            <div key={r.rule_id} data-testid={`cls-card-${r.rule_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-display text-base font-bold tracking-tight">{r.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{r.rule_id}</div>
                </div>
                {lab && <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm" style={{ borderColor: lab.color + "66", color: lab.color, background: lab.color + "1a" }}>→ {lab.name}</span>}
              </div>
              <div className="text-xs text-zinc-400 leading-relaxed min-h-[2em]">{r.description || <span className="text-zinc-600">no description</span>}</div>
              <div className="font-mono text-xs space-y-1 text-zinc-300 mt-2">
                <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">DLP HOOKS</span>{r.dlp_rule_ids?.length || 0}</div>
                <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">MIN CONF</span><span className="text-amber-400">{r.min_confidence}</span></div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
                <span className={"text-[10px] font-mono uppercase tracking-widest " + (r.enabled ? "text-emerald-400" : "text-zinc-500")}>{r.enabled ? "ENABLED" : "DISABLED"}</span>
                {user?.role === "admin" && (
                  <Button size="sm" variant="ghost" data-testid={`delete-cls-${r.rule_id}`} onClick={() => del(r.rule_id)} className="gap-1.5 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
                )}
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no classifier policies yet.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
